import type { MarkdownAugment } from "@yep-anywhere/shared";
import type { ContentBlock, Message } from "../types";
import type {
  RenderItem,
  SessionSetupItem,
  SystemItem,
  ToolCallItem,
  ToolResultData,
  UserPromptItem,
} from "../types/renderItems";
import {
  formatCommandTurn,
  isCompactionLocalCommandOutput,
  isLocalCommandCaveatOnly,
  parseCommandTurn,
  parseLocalCommandStdout,
} from "./commandTurn";
import {
  isLegacyCodexEnvironmentContextText,
  isLegacyCodexSetupText,
} from "./codexLegacySetup";
import { getMessageId } from "./mergeMessages";
import {
  extractDetachedCellId,
  getCommandResultMeta,
  parseShellToolOutput,
} from "./shellToolOutput";
import {
  isTaskNotificationMessage,
  parseTaskNotification,
} from "./parseTaskNotification";

const AWAY_SUMMARY_HINT_SUFFIX_RE = /\s*\(disable recaps in \/config\)\s*$/u;

export function stripAwaySummaryHintSuffix(content: string): string {
  return content.replace(AWAY_SUMMARY_HINT_SUFFIX_RE, "");
}

/**
 * When true, indicates the session has active tool work or approval.
 * Orphaned tools in the current trailing user turn are treated as pending.
 *
 * This handles the case where multiple tools are queued for approval while
 * still allowing older orphaned tools from prior turns to render interrupted.
 */
export type ActiveToolApproval = boolean;

/**
 * Augments to embed into RenderItems during preprocessing.
 * These are pre-computed on the server for completed messages.
 */
export interface PreprocessAugments {
  /** Pre-rendered markdown HTML keyed by message ID */
  markdown?: Record<string, MarkdownAugment>;
  /** Active tool approval request - if present, matching tool_use won't be marked aborted */
  activeToolApproval?: ActiveToolApproval;
}

/**
 * Preprocess messages into render items, pairing tool_use with tool_result.
 *
 * This is a pure function - given the same messages, returns the same items.
 * Safe to call on every render (use useMemo).
 *
 * Results are additionally cached by input identity so a remounted view
 * (route switch back, tab raise) whose messages array survived in the
 * session-detail cache reuses the previous computation instead of
 * re-running the whole pipeline. This relies on two standing contracts:
 * message arrays are replaced on change, never mutated in place
 * (mergeJSONLMessages builds a new array; no-op filters return the same
 * one), and returned render items are treated as immutable downstream.
 */
interface PreprocessCacheEntry {
  markdown: PreprocessAugments["markdown"];
  activeToolApproval: boolean | undefined;
  items: RenderItem[];
}

const preprocessResultCache = new WeakMap<Message[], PreprocessCacheEntry[]>();
const PREPROCESS_CACHE_VARIANTS_PER_ARRAY = 3;

export function preprocessMessages(
  messages: Message[],
  augments?: PreprocessAugments,
): RenderItem[] {
  const markdown = augments?.markdown;
  const activeToolApproval = augments?.activeToolApproval;
  const cachedVariants = preprocessResultCache.get(messages);
  const cached = cachedVariants?.find(
    (entry) =>
      entry.markdown === markdown &&
      entry.activeToolApproval === activeToolApproval,
  );
  if (cached) {
    return cached.items;
  }

  const computed = computePreprocessedMessages(messages, augments);
  const variants = cachedVariants ?? [];
  variants.push({ markdown, activeToolApproval, items: computed });
  if (variants.length > PREPROCESS_CACHE_VARIANTS_PER_ARRAY) {
    variants.shift();
  }
  preprocessResultCache.set(messages, variants);
  return computed;
}

function computePreprocessedMessages(
  messages: Message[],
  augments?: PreprocessAugments,
): RenderItem[] {
  const items: RenderItem[] = [];
  const toolCallIndices = new Map<string, number>(); // tool_use_id → index in items
  const pendingToolCalls = new Map<string, number>(); // tool_use_id → index in items
  const configAckState = { lastSignature: null as string | null };

  const orphanedToolIds = collectOrphanedToolIds(
    messages,
    augments?.activeToolApproval === true,
  );

  for (const msg of messages) {
    processMessage(
      msg,
      items,
      toolCallIndices,
      pendingToolCalls,
      orphanedToolIds,
      configAckState,
      augments,
    );
  }

  const compactCoalescedItems = coalesceCompactBoundaryItems(items);
  const slashCommandCoalescedItems = coalesceSlashCommandSkillBodies(
    compactCoalescedItems,
  );
  const enrichedItems = enrichWriteStdinWithCommand(slashCommandCoalescedItems);
  const pollCoalescedItems = coalesceDetachedPollContinuations(enrichedItems);
  const backgroundAnnotatedItems = annotateBackgroundCommands(
    pollCoalescedItems,
  );
  const shellPollFilteredItems = hideContextFreeEmptyShellPolls(
    backgroundAnnotatedItems,
  );
  return collapseSessionSetupRuns(shellPollFilteredItems);
}

function collectOrphanedToolIds(
  messages: Message[],
  suppressCurrentTurnOrphans: boolean,
): Set<string> {
  const orphanedToolIds = new Set<string>();
  const suppressFromIndex = suppressCurrentTurnOrphans
    ? findLastUserPromptMessageIndex(messages)
    : messages.length;

  for (let index = 0; index < messages.length; index++) {
    const msg = messages[index];
    if (!msg?.orphanedToolUseIds) {
      continue;
    }

    if (suppressCurrentTurnOrphans && index >= suppressFromIndex) {
      continue;
    }

    for (const id of msg.orphanedToolUseIds) {
      orphanedToolIds.add(id);
    }
  }

  return orphanedToolIds;
}

function findLastUserPromptMessageIndex(messages: Message[]): number {
  for (let index = messages.length - 1; index >= 0; index--) {
    const msg = messages[index];
    if (msg && isUserPromptMessage(msg)) {
      return index;
    }
  }
  return 0;
}

const RESUME_ENVIRONMENT_CONTEXT_MAX_GAP_MS = 5_000;

const INTERNAL_REASONING_PLACEHOLDER = "Reasoning [internal]";

function getPromptText(content: string | ContentBlock[]): string {
  if (typeof content === "string") {
    return content;
  }
  return content
    .filter(
      (block): block is ContentBlock & { type: "text"; text: string } =>
        block.type === "text" && typeof block.text === "string",
    )
    .map((block) => block.text)
    .join("\n");
}

function getPreprocessMessageContent(
  msg: Message,
): string | ContentBlock[] | undefined {
  return (
    (msg.message as { content?: string | ContentBlock[] } | undefined)
      ?.content ?? msg.content
  );
}

function isCompactSummaryMessage(msg: Message): boolean {
  return msg.isCompactSummary === true;
}

function isCompactCommand(command: string): boolean {
  const normalized = command.trim().replace(/^\/+/, "").toLowerCase();
  return normalized === "compact" || normalized === "compress";
}

function systemLocalCommandContent(content: unknown): string | null {
  if (typeof content !== "string") {
    return null;
  }

  if (isLocalCommandCaveatOnly(content)) {
    return null;
  }

  const commandTurn = parseCommandTurn(content);
  if (commandTurn) {
    return isCompactCommand(commandTurn.command)
      ? null
      : formatCommandTurn(commandTurn);
  }

  const localCommandStdout = parseLocalCommandStdout(content);
  if (localCommandStdout !== null) {
    if (!localCommandStdout) {
      return null;
    }
    return isCompactionLocalCommandOutput(localCommandStdout)
      ? null
      : localCommandStdout;
  }

  const trimmedContent = content.trim();
  return trimmedContent ? trimmedContent : null;
}

function compactMetadataDetail(msg: Message): string | null {
  const metadata = (msg as { compactMetadata?: unknown }).compactMetadata;
  if (!isRecord(metadata)) {
    return null;
  }
  return `compactMetadata:\n${JSON.stringify(metadata, null, 2)}`;
}

function compactBoundaryDetails(msg: Message): Array<string | ContentBlock[]> {
  const details: Array<string | ContentBlock[]> = [];
  const metadata = compactMetadataDetail(msg);
  if (metadata) {
    details.push(metadata);
  }
  return details;
}

function compactSummaryDetails(
  content: string | ContentBlock[] | undefined,
): Array<string | ContentBlock[]> {
  return content === undefined ? [] : [content];
}

function contentBlocksText(content: string | ContentBlock[]): string {
  if (typeof content === "string") {
    return content;
  }
  return content
    .map((block) =>
      block.type === "text" && typeof block.text === "string" ? block.text : "",
    )
    .filter(Boolean)
    .join("\n");
}

function isCompactBoundaryItem(
  item: RenderItem,
): item is SystemItem & { subtype: "compact_boundary" } {
  return item.type === "system" && item.subtype === "compact_boundary";
}

function hasSystemCompactBoundarySource(item: SystemItem): boolean {
  return item.sourceMessages.some(
    (source) =>
      source.type === "system" &&
      (source as { subtype?: string }).subtype === "compact_boundary",
  );
}

function mergeCompactBoundaryRun(
  run: Array<SystemItem & { subtype: "compact_boundary" }>,
): SystemItem {
  const first = run[0];
  if (!first) {
    throw new Error("Cannot merge an empty compact boundary run");
  }
  const preferred = run.find(hasSystemCompactBoundarySource) ?? first;
  const sourceMessages = run.flatMap((item) => item.sourceMessages);
  const details = run.flatMap((item) => item.details ?? []);
  return {
    type: "system",
    id: preferred.id,
    subtype: "compact_boundary",
    content: preferred.content,
    status: preferred.status,
    configChanged: preferred.configChanged,
    isSubagent: preferred.isSubagent,
    sourceMessages,
    details: details.length > 0 ? details : undefined,
  };
}

function coalesceCompactBoundaryItems(items: RenderItem[]): RenderItem[] {
  const coalesced: RenderItem[] = [];
  let index = 0;

  while (index < items.length) {
    const item = items[index];
    if (!item || !isCompactBoundaryItem(item)) {
      if (item) {
        coalesced.push(item);
      }
      index += 1;
      continue;
    }

    const run: Array<SystemItem & { subtype: "compact_boundary" }> = [item];
    let runIndex = index + 1;
    while (runIndex < items.length) {
      const runItem = items[runIndex];
      if (!runItem || !isCompactBoundaryItem(runItem)) {
        break;
      }
      run.push(runItem);
      runIndex += 1;
    }
    coalesced.push(mergeCompactBoundaryRun(run));
    index = runIndex;
  }

  return coalesced;
}

function isLocalCommandItem(
  item: RenderItem,
): item is SystemItem & { subtype: "local_command" } {
  return item.type === "system" && item.subtype === "local_command";
}

function isSlashCommandSkillBodyItem(item: RenderItem): item is UserPromptItem {
  if (item.type !== "user_prompt") {
    return false;
  }
  if (!item.sourceMessages.some((message) => message.isMeta === true)) {
    return false;
  }
  return contentBlocksText(item.content)
    .trimStart()
    .startsWith("Base directory for this skill:");
}

function messagePromptId(message: Message): string | null {
  const promptId = (message as { promptId?: unknown }).promptId;
  return typeof promptId === "string" && promptId ? promptId : null;
}

function isLinkedSlashCommandSkillBody(
  commandItem: SystemItem,
  skillItem: UserPromptItem,
): boolean {
  const commandIds = new Set(
    commandItem.sourceMessages.map(getMessageId).filter(Boolean),
  );
  const skillParentUuids = skillItem.sourceMessages
    .map((message) =>
      typeof message.parentUuid === "string" ? message.parentUuid : null,
    )
    .filter((parentUuid): parentUuid is string => parentUuid !== null);
  if (skillParentUuids.length > 0 && commandIds.size > 0) {
    return skillParentUuids.some((parentUuid) => commandIds.has(parentUuid));
  }

  const commandPromptIds = new Set(
    commandItem.sourceMessages
      .map(messagePromptId)
      .filter((promptId): promptId is string => promptId !== null),
  );
  const skillPromptIds = skillItem.sourceMessages
    .map(messagePromptId)
    .filter((promptId): promptId is string => promptId !== null);
  if (skillPromptIds.length > 0 && commandPromptIds.size > 0) {
    return skillPromptIds.some((promptId) => commandPromptIds.has(promptId));
  }

  return true;
}

function mergeSlashCommandSkillBody(
  commandItem: SystemItem & { subtype: "local_command" },
  skillItem: UserPromptItem,
): SystemItem {
  return {
    ...commandItem,
    sourceMessages: [
      ...commandItem.sourceMessages,
      ...skillItem.sourceMessages,
    ],
    details: [...(commandItem.details ?? []), skillItem.content],
  };
}

function coalesceSlashCommandSkillBodies(items: RenderItem[]): RenderItem[] {
  const coalesced: RenderItem[] = [];
  let index = 0;

  while (index < items.length) {
    const item = items[index];
    const nextItem = items[index + 1];
    if (
      item &&
      nextItem &&
      isLocalCommandItem(item) &&
      isSlashCommandSkillBodyItem(nextItem) &&
      isLinkedSlashCommandSkillBody(item, nextItem)
    ) {
      coalesced.push(mergeSlashCommandSkillBody(item, nextItem));
      index += 2;
      continue;
    }

    if (item) {
      coalesced.push(item);
    }
    index += 1;
  }

  return coalesced;
}

function isSlashCommandSkillBodyMessage(msg: Message): boolean {
  const content = getPreprocessMessageContent(msg);
  return (
    msg.isMeta === true &&
    content !== undefined &&
    contentBlocksText(content)
      .trimStart()
      .startsWith("Base directory for this skill:")
  );
}

function isUserPromptMessage(msg: Message): boolean {
  const content = getPreprocessMessageContent(msg);
  const role =
    (msg.message as { role?: "user" | "assistant" } | undefined)?.role ??
    msg.role;
  const isUserMessage = msg.type === "user" || role === "user";
  if (!isUserMessage) {
    return false;
  }
  // Task notifications arrive as user-role entries but are SDK-injected, not
  // user-authored — they must not anchor the "last user prompt" affordances.
  if (isTaskNotificationMessage(msg)) {
    return false;
  }
  if (isCompactSummaryMessage(msg)) {
    return false;
  }
  if (isSlashCommandSkillBodyMessage(msg)) {
    return false;
  }
  if (Array.isArray(content)) {
    return !content.every((block) => block.type === "tool_result");
  }
  if (typeof content !== "string") {
    return false;
  }
  if (msg.isMeta === true && isLocalCommandCaveatOnly(content)) {
    return false;
  }
  if (parseLocalCommandStdout(content) !== null) {
    return false;
  }
  return !parseCommandTurn(content);
}

function isDisplayableThinking(
  thinking: string | undefined,
): thinking is string {
  const trimmed = thinking?.trim();
  return !!trimmed && trimmed !== INTERNAL_REASONING_PLACEHOLDER;
}

function isSessionSetupPrompt(item: UserPromptItem): boolean {
  const text = getPromptText(item.content).trimStart();
  return isLegacyCodexSetupText(text, item.sourceMessages);
}

function isEnvironmentContextSetupPrompt(item: UserPromptItem): boolean {
  return isLegacyCodexEnvironmentContextText(
    getPromptText(item.content),
    item.sourceMessages,
  );
}

function itemTimestampMs(item: RenderItem): number | null {
  const timestamp = item.sourceMessages
    .map((message) =>
      typeof message.timestamp === "string"
        ? Date.parse(message.timestamp)
        : NaN,
    )
    .find(Number.isFinite);
  return timestamp === undefined ? null : timestamp;
}

function isImmediateResumeEnvironmentContext(
  setupItem: UserPromptItem,
  nextItem: RenderItem | undefined,
): boolean {
  if (
    !isEnvironmentContextSetupPrompt(setupItem) ||
    nextItem?.type !== "user_prompt" ||
    isSessionSetupPrompt(nextItem)
  ) {
    return false;
  }

  const setupMs = itemTimestampMs(setupItem);
  const nextMs = itemTimestampMs(nextItem);
  if (setupMs === null || nextMs === null) {
    return false;
  }

  const gapMs = nextMs - setupMs;
  return gapMs >= 0 && gapMs <= RESUME_ENVIRONMENT_CONTEXT_MAX_GAP_MS;
}

function collapseSessionSetupRuns(items: RenderItem[]): RenderItem[] {
  const result: RenderItem[] = [];
  let index = 0;

  while (index < items.length) {
    const item = items[index];
    if (item?.type !== "user_prompt" || !isSessionSetupPrompt(item)) {
      result.push(item as RenderItem);
      index += 1;
      continue;
    }

    const setupItems: UserPromptItem[] = [];
    let runIndex = index;
    while (runIndex < items.length) {
      const runItem = items[runIndex];
      if (runItem?.type !== "user_prompt" || !isSessionSetupPrompt(runItem)) {
        break;
      }
      setupItems.push(runItem);
      runIndex += 1;
    }

    const singleSetupItem = setupItems.length === 1 ? setupItems[0] : undefined;
    const shouldSuppressSingleSetupItem =
      singleSetupItem !== undefined &&
      isImmediateResumeEnvironmentContext(singleSetupItem, items[runIndex]);

    if (shouldSuppressSingleSetupItem) {
      index = runIndex;
      continue;
    }

    // Preserve likely user-authored single setup-like messages mid-session.
    // Collapse any run at session start and any multi-item run (typical resume preamble).
    if (setupItems.length > 1 || index === 0) {
      const firstSetupItem = setupItems[0];
      if (!firstSetupItem) {
        index = runIndex;
        continue;
      }

      const collapsedItem: SessionSetupItem = {
        type: "session_setup",
        id: `session-setup-${firstSetupItem.id}`,
        title: "Session setup",
        prompts: setupItems.map((setupItem) => setupItem.content),
        sourceMessages: setupItems.flatMap(
          (setupItem) => setupItem.sourceMessages,
        ),
      };
      result.push(collapsedItem);
    } else {
      const singleSetupItem = setupItems[0];
      if (singleSetupItem) {
        result.push(singleSetupItem);
      }
    }

    index = runIndex;
  }

  return result;
}

function processMessage(
  msg: Message,
  items: RenderItem[],
  toolCallIndices: Map<string, number>,
  pendingToolCalls: Map<string, number>,
  orphanedToolIds: Set<string>,
  configAckState: { lastSignature: string | null },
  augments?: PreprocessAugments,
): void {
  const msgId = getMessageId(msg);

  // Handle provider/runtime error entries as visible system messages.
  if (msg.type === "error") {
    const errorText =
      (typeof msg.error === "string" && msg.error) ||
      (typeof msg.content === "string" && msg.content) ||
      "Agent error";
    const systemItem: SystemItem = {
      type: "system",
      id: msgId || `error-${msg.timestamp ?? Date.now()}`,
      subtype: msg.codexWillRetry === true ? "warning" : "error",
      content: errorText,
      sourceMessages: [msg],
    };
    items.push(systemItem);
    return;
  }

  // Handle system entries (compact_boundary, status, etc.)
  if (msg.type === "system") {
    const subtype = (msg as { subtype?: string }).subtype ?? "unknown";
    if (subtype === "local_command") {
      const content = systemLocalCommandContent(msg.content);
      if (content !== null) {
        items.push({
          type: "system",
          id: msgId,
          subtype,
          content,
          sourceMessages: [msg],
          isSubagent: msg.isSubagent,
        });
      }
      return;
    }

    // Render compact_boundary as a visible system message
    if (
      subtype === "compact_boundary" ||
      subtype === "turn_aborted" ||
      subtype === "config_ack" ||
      subtype === "away_summary" ||
      subtype === "subagent_activity"
    ) {
      const configSignature =
        subtype === "config_ack" ? getConfigAckSignature(msg) : null;
      const content =
        typeof msg.content === "string"
          ? msg.content
          : subtype === "turn_aborted"
            ? "Turn aborted"
            : subtype === "config_ack"
              ? "Configuration updated"
              : subtype === "away_summary"
                ? "Recap unavailable"
                : subtype === "subagent_activity"
                  ? "Subagent updated"
                  : "Context compacted";
      const systemItem: SystemItem = {
        type: "system",
        id: msgId,
        subtype,
        content:
          subtype === "away_summary"
            ? stripAwaySummaryHintSuffix(content)
            : content,
        sourceMessages: [msg],
        ...(subtype === "compact_boundary"
          ? {
              details: compactBoundaryDetails(msg),
            }
          : {}),
        ...(subtype === "config_ack"
          ? {
              configChanged:
                msg.configMismatch === true &&
                configSignature !== null &&
                configSignature !== configAckState.lastSignature,
            }
          : {}),
      };
      items.push(systemItem);
      if (subtype === "config_ack" && configSignature !== null) {
        configAckState.lastSignature = configSignature;
      }
    }
    // Status messages (compacting indicator) are transient - handled separately via isCompacting state
    // Skip other system entries (init, status, etc.) - they're internal
    return;
  }

  // Debug logging for streaming transition issues
  if (
    typeof window !== "undefined" &&
    window.__STREAMING_DEBUG__ &&
    msg.type === "assistant"
  ) {
    console.log("[preprocessMessages] Processing assistant message:", {
      msgId,
      uuid: msg.uuid,
      id: msg.id,
      _isStreaming: msg._isStreaming,
    });
  }

  // Get content from nested message object (SDK structure) first, fall back to top-level
  // Phase 4c: prefer message.content over top-level content
  const content =
    (msg.message as { content?: string | ContentBlock[] } | undefined)
      ?.content ?? msg.content;

  // Use type for discrimination (SDK field), fall back to role for legacy data
  // Phase 4c: prefer type over role, but maintain backward compatibility
  const role =
    (msg.message as { role?: "user" | "assistant" } | undefined)?.role ??
    msg.role;
  const isUserMessage = msg.type === "user" || role === "user";

  // String content = user prompt (only if type is user)
  if (typeof content === "string") {
    if (isUserMessage) {
      if (isCompactSummaryMessage(msg)) {
        items.push({
          type: "system",
          id: msgId,
          subtype: "compact_boundary",
          content: "Context compacted",
          details: compactSummaryDetails(content),
          sourceMessages: [msg],
          isSubagent: msg.isSubagent,
        });
        return;
      }
      if (msg.isMeta === true && isLocalCommandCaveatOnly(content)) {
        return;
      }
      const commandTurn = parseCommandTurn(content);
      if (commandTurn) {
        if (isCompactCommand(commandTurn.command)) {
          return;
        }
        items.push({
          type: "system",
          id: msgId,
          subtype: "local_command",
          content: formatCommandTurn(commandTurn),
          sourceMessages: [msg],
          isSubagent: msg.isSubagent,
        });
        return;
      }
      const localCommandStdout = parseLocalCommandStdout(content);
      if (localCommandStdout !== null) {
        if (!localCommandStdout) {
          return;
        }
        if (isCompactionLocalCommandOutput(localCommandStdout)) {
          return;
        }
        items.push({
          type: "system",
          id: msgId,
          subtype: "local_command",
          content: localCommandStdout,
          sourceMessages: [msg],
          isSubagent: msg.isSubagent,
        });
        return;
      }
      // SDK-injected task notifications render as a system/event chip, not a
      // user bubble. Gated on origin.kind (non-heuristic), then the XML body is
      // parsed for the chip's structured fields.
      if (isTaskNotificationMessage(msg)) {
        const parsed = parseTaskNotification(content);
        items.push({
          type: "task_notification",
          id: msgId,
          raw: content,
          sourceMessages: [msg],
          isSubagent: msg.isSubagent,
          ...parsed,
        });
        return;
      }
      items.push({
        type: "user_prompt",
        id: msgId,
        content,
        sourceMessages: [msg],
        isSubagent: msg.isSubagent,
      });
      return;
    }
    // Assistant message with string content - convert to text block
    if (content.trim()) {
      const messageHtml = (msg as { _html?: string })._html;
      items.push({
        type: "text",
        id: msgId,
        text: content,
        sourceMessages: [msg],
        isSubagent: msg.isSubagent,
        augmentHtml: messageHtml ?? augments?.markdown?.[msgId]?.html,
      });
    }
    return;
  }

  // Not an array - shouldn't happen but handle gracefully
  if (!Array.isArray(content)) {
    return;
  }

  // Check if this is a user message with only tool_result blocks
  const isToolResultMessage =
    isUserMessage && content.every((b) => b.type === "tool_result");

  if (isToolResultMessage) {
    // Attach results to pending tool calls
    for (const block of content) {
      if (block.type === "tool_result" && block.tool_use_id) {
        attachToolResult(block, msg, items, pendingToolCalls);
      }
    }
    return;
  }

  // Check if this is a real user prompt (not tool results)
  if (isUserMessage) {
    items.push({
      type: "user_prompt",
      id: msgId,
      content,
      sourceMessages: [msg],
      isSubagent: msg.isSubagent,
    });
    return;
  }

  // Assistant message - process each block
  // First pass: find the last text block index (for streaming cursor placement)
  let lastTextBlockIndex = -1;
  if (msg._isStreaming) {
    for (let i = content.length - 1; i >= 0; i--) {
      const block = content[i];
      if (block?.type === "text" && block.text?.trim()) {
        lastTextBlockIndex = i;
        break;
      }
    }
  }

  for (let i = 0; i < content.length; i++) {
    const block = content[i];
    if (!block) continue;

    const blockId = `${msgId}-${i}`;

    if (block.type === "text") {
      if (block.text?.trim()) {
        // Get _html from server-injected augment, fall back to markdownAugments (for SSE path)
        const blockHtml = (block as { _html?: string })._html;
        items.push({
          type: "text",
          id: blockId,
          text: block.text,
          sourceMessages: [msg],
          isSubagent: msg.isSubagent,
          // Only show streaming cursor on the last text block
          isStreaming: msg._isStreaming && i === lastTextBlockIndex,
          // Prefer inline _html from server, fall back to markdownAugments (SSE path)
          augmentHtml: blockHtml ?? augments?.markdown?.[msgId]?.html,
        });
      }
    } else if (block.type === "thinking") {
      const thinking = block.thinking;
      if (isDisplayableThinking(thinking)) {
        items.push({
          type: "thinking",
          id: blockId,
          thinking,
          signature: undefined,
          status: msg._isStreaming ? "streaming" : "complete",
          sourceMessages: [msg],
          isSubagent: msg.isSubagent,
        });
      }
    } else if (block.type === "tool_use") {
      if (block.id && block.name) {
        // Stream reconnects/resume can replay the same tool_use id from a
        // different assistant message snapshot. Keep one render item per tool id.
        const existingIndex = toolCallIndices.get(block.id);
        if (existingIndex !== undefined) {
          const existingItem = items[existingIndex];
          if (existingItem?.type === "tool_call") {
            items[existingIndex] = updateToolCallSnapshot(
              existingItem,
              msg,
              block.input,
              block._displayActions,
            );
            if (existingItem.status === "pending") {
              pendingToolCalls.set(block.id, existingIndex);
            }
          }
          continue;
        }

        // Check if this tool call is missing a result after the turn boundary.
        // That is not the same as an explicit interruption: Codex/YA may have
        // missed the result event even though a side effect, such as an edit,
        // landed in the filesystem.
        const isOrphaned = orphanedToolIds.has(block.id);
        const toolCall: ToolCallItem = {
          type: "tool_call",
          id: block.id,
          toolName: block.name,
          toolInput: block.input,
          ...(block._displayActions
            ? { displayActions: block._displayActions }
            : {}),
          toolResult: undefined,
          status: isOrphaned ? "incomplete" : "pending",
          sourceMessages: [msg],
          isSubagent: msg.isSubagent,
        };
        const itemIndex = items.length;
        toolCallIndices.set(block.id, itemIndex);
        pendingToolCalls.set(block.id, itemIndex);
        items.push(toolCall);
      }
    }
  }
}

function getConfigAckSignature(msg: Message): string | null {
  const configModel =
    typeof msg.configModel === "string" ? msg.configModel.trim() : "";
  const configThinking =
    typeof msg.configThinking === "string" ? msg.configThinking.trim() : "";
  if (configModel || configThinking) {
    return `${configModel}::${configThinking}`;
  }
  return typeof msg.content === "string" ? msg.content.trim() : null;
}

function appendSourceMessage(
  item: ToolCallItem,
  message: Message,
): ToolCallItem {
  const messageId = getMessageId(message);
  if (
    item.sourceMessages.some((source) => getMessageId(source) === messageId)
  ) {
    return item;
  }
  return {
    ...item,
    sourceMessages: [...item.sourceMessages, message],
  };
}

function updateToolCallSnapshot(
  item: ToolCallItem,
  message: Message,
  toolInput: unknown,
  displayActions: ToolCallItem["displayActions"],
): ToolCallItem {
  const withSource = appendSourceMessage(item, message);
  return {
    ...withSource,
    toolInput,
    displayActions,
  };
}

/**
 * Parse Agent tool result from text content blocks (SDK 0.2.76+).
 *
 * New SDK embeds agentId and usage stats in text rather than a structured
 * tool_use_result. Example text block:
 *   "agentId: abc123 (for resuming...)\n<usage>total_tokens: 1234\ntool_uses: 5\nduration_ms: 6789</usage>"
 *
 * Returns a TaskResult-shaped object for the renderer, or undefined if not parseable.
 */
export function parseAgentResultFromText(
  block: ContentBlock,
): Record<string, unknown> | undefined {
  // Content may be a string or array of content blocks
  const texts: string[] = [];
  if (typeof block.content === "string") {
    texts.push(block.content);
  } else if (Array.isArray(block.content)) {
    for (const cb of block.content as Array<{ type?: string; text?: string }>) {
      if (cb.type === "text" && cb.text) texts.push(cb.text);
    }
  }

  const fullText = texts.join("\n");
  if (!fullText) return undefined;

  const displayContent = extractAgentDisplayContent(block);

  // Extract agentId
  const agentIdMatch = fullText.match(/^agentId:\s*(\S+)/m);
  if (!agentIdMatch) return undefined;

  const result: Record<string, unknown> = {
    agentId: agentIdMatch[1],
    status: "completed",
  };
  if (displayContent && displayContent.length > 0) {
    result.content = displayContent;
  }

  // Extract usage stats from <usage> block
  const usageMatch = fullText.match(/<usage>([\s\S]*?)<\/usage>/);
  if (usageMatch?.[1]) {
    const usage = usageMatch[1];
    const tokens = usage.match(/total_tokens:\s*(\d+)/);
    const tools = usage.match(/tool_uses:\s*(\d+)/);
    const duration = usage.match(/duration_ms:\s*(\d+)/);
    if (tokens?.[1]) result.totalTokens = Number(tokens[1]);
    if (tools?.[1]) result.totalToolUseCount = Number(tools[1]);
    if (duration?.[1]) result.totalDurationMs = Number(duration[1]);
  }

  return result;
}

function stripAgentMetadata(text: string): string {
  return text
    .replace(/^agentId:\s*\S+.*$/gm, "")
    .replace(/<usage>[\s\S]*?<\/usage>/g, "")
    .trim();
}

function extractAgentDisplayContent(
  block: ContentBlock,
): ContentBlock[] | undefined {
  if (typeof block.content === "string") {
    const text = stripAgentMetadata(block.content);
    return text ? [{ type: "text", text }] : undefined;
  }

  if (!Array.isArray(block.content)) {
    return undefined;
  }

  const displayBlocks: ContentBlock[] = [];
  for (const contentBlock of block.content) {
    if (!contentBlock || typeof contentBlock !== "object") {
      continue;
    }

    if (contentBlock.type === "text" && typeof contentBlock.text === "string") {
      const text = stripAgentMetadata(contentBlock.text);
      if (!text) {
        continue;
      }
      displayBlocks.push({ ...contentBlock, text });
      continue;
    }

    displayBlocks.push(contentBlock as ContentBlock);
  }

  return displayBlocks.length > 0 ? displayBlocks : undefined;
}

function attachToolResult(
  block: ContentBlock,
  resultMessage: Message,
  items: RenderItem[],
  pendingToolCalls: Map<string, number>,
): void {
  const toolUseId = block.tool_use_id;
  if (!toolUseId) return;

  const index = pendingToolCalls.get(toolUseId);
  if (index === undefined) {
    // Orphan result - shouldn't happen normally
    console.warn(`Tool result for unknown tool_use: ${toolUseId}`);
    return;
  }

  const item = items[index];
  if (item?.type !== "tool_call") return;

  // Attach result to existing tool call
  // Handle both camelCase (toolUseResult) and snake_case (tool_use_result) from SDK
  let structured =
    resultMessage.toolUseResult ??
    (resultMessage as Record<string, unknown>).tool_use_result;

  // SDK 0.2.76+: Agent tool has no structured tool_use_result.
  // Parse agentId and usage stats from the text content blocks instead.
  if (!structured && (item.toolName === "Agent" || item.toolName === "Task")) {
    structured = parseAgentResultFromText(block);
  }

  const resultData: ToolResultData = {
    content: typeof block.content === "string" ? block.content : "",
    isError: block.is_error || false,
    structured,
  };
  const isBackgroundProcessResult = isBackgroundProcessToolResult(
    block,
    item.toolName,
  );
  const isInterruptedProcessResult = isInterruptedToolResult(
    block,
    resultData,
    item.toolName,
  );

  // Create a new ToolCallItem to ensure React sees the change
  let status: ToolCallItem["status"] = "complete";
  if (isInterruptedProcessResult || item.status === "aborted") {
    status = "aborted";
  } else if (isBackgroundProcessResult) {
    status = item.status === "incomplete" ? "incomplete" : "pending";
  } else if (block.is_error) {
    status = "error";
  }
  const updatedItem: ToolCallItem = {
    type: "tool_call",
    id: item.id,
    toolName: item.toolName,
    toolInput: item.toolInput,
    ...(item.displayActions ? { displayActions: item.displayActions } : {}),
    toolResult: resultData,
    status,
    sourceMessages: appendSourceMessage(item, resultMessage).sourceMessages,
    isSubagent: item.isSubagent,
  };

  items[index] = updatedItem;
  if (!isBackgroundProcessResult && !isInterruptedProcessResult) {
    pendingToolCalls.delete(toolUseId);
  }
}

function isBackgroundProcessToolResult(
  block: ContentBlock,
  toolName: string,
): boolean {
  if (toolName !== "Bash") {
    return false;
  }
  const content = typeof block.content === "string" ? block.content : "";
  if (!content) {
    return false;
  }
  return (
    /(?:^|\n)\s*(?:Process\s+running\s+with\s+session\s+ID|session(?:\s+id)?)\s*:?\s*\d+\b/i.test(
      content,
    ) &&
    !/(?:^|\n)\s*(?:Exit code:|Process exited with code)\s*-?\d+\b/i.test(
      content,
    )
  );
}

function isInterruptedToolResult(
  block: ContentBlock,
  result: ToolResultData,
  toolName: string,
): boolean {
  if (toolName !== "Bash") {
    return false;
  }
  if (
    result.structured &&
    typeof result.structured === "object" &&
    (result.structured as { interrupted?: unknown }).interrupted === true
  ) {
    return true;
  }
  const content = typeof block.content === "string" ? block.content : "";
  return /(?:^|\n)\s*(?:aborted by user|interrupted by user)(?:\s|$)/i.test(
    content,
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function extractCommandFromInput(input: unknown): string | undefined {
  if (!isRecord(input)) {
    return undefined;
  }
  if (typeof input.command === "string" && input.command.trim().length > 0) {
    return input.command;
  }
  if (typeof input.cmd === "string" && input.cmd.trim().length > 0) {
    return input.cmd;
  }
  return undefined;
}

function coerceSessionId(value: unknown): string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(Math.trunc(value));
  }
  if (typeof value === "string" && value.trim().length > 0) {
    return value.trim();
  }
  return undefined;
}

function extractSessionIdFromWriteStdinInput(
  input: unknown,
): string | undefined {
  if (!isRecord(input)) {
    return undefined;
  }
  return coerceSessionId(input.session_id ?? input.sessionId);
}

function extractSessionIdFromToolResult(
  item: ToolCallItem,
): string | undefined {
  const structured = item.toolResult?.structured;
  if (isRecord(structured)) {
    const fromStructured = coerceSessionId(
      structured.session_id ?? structured.sessionId,
    );
    if (fromStructured) {
      return fromStructured;
    }
  }

  const raw = item.toolResult?.content ?? "";
  const text = typeof raw === "string" ? raw : "";
  // Accept provider envelopes and the SESSION_ID=N convention code-mode
  // scripts print precisely so the session can be reconnected later.
  const match = text.match(
    /(?:^|\n)\s*(?:Process\s+running\s+with\s+session\s+ID|session[_\s]?id|session)\s*[:=]?\s*(\d+)\b/i,
  );
  if (!match?.[1]) {
    return undefined;
  }
  return match[1];
}

function extractCellIdFromWriteStdinInput(input: unknown): string | undefined {
  if (!isRecord(input)) {
    return undefined;
  }
  return coerceSessionId(input.cell_id ?? input.cellId);
}

/** A code-mode script that outlives its yield window detaches into a cell
 * ("Script running with cell ID N"); a later `wait` call polls that cell. */
function extractCellIdFromToolResult(item: ToolCallItem): string | undefined {
  const raw = item.toolResult?.content ?? "";
  return extractDetachedCellId(typeof raw === "string" ? raw : "");
}

function withLinkedCommand(input: unknown, command: string): unknown {
  if (!isRecord(input)) {
    return input;
  }
  if (typeof input.linked_command === "string" && input.linked_command.trim()) {
    return input;
  }
  return { ...input, linked_command: command };
}

function withLinkedFilePath(input: unknown, filePath: string): unknown {
  if (!isRecord(input)) {
    return input;
  }
  if (
    typeof input.linked_file_path === "string" &&
    input.linked_file_path.trim()
  ) {
    return input;
  }
  return { ...input, linked_file_path: filePath };
}

function withLinkedToolName(input: unknown, toolName: string): unknown {
  if (!isRecord(input)) {
    return input;
  }
  if (
    typeof input.linked_tool_name === "string" &&
    input.linked_tool_name.trim()
  ) {
    return input;
  }
  return { ...input, linked_tool_name: toolName };
}

function isCommandSessionToolName(toolName: string): boolean {
  const normalized = toolName.toLowerCase();
  return (
    normalized === "bash" ||
    normalized === "exec_command" ||
    normalized === "shell_command"
  );
}

function isFileSessionToolName(toolName: string): boolean {
  const normalized = toolName.toLowerCase();
  return (
    normalized === "read" || normalized === "write" || normalized === "edit"
  );
}

function extractFilePathFromToolInput(input: unknown): string | undefined {
  if (!isRecord(input) || typeof input.file_path !== "string") {
    return undefined;
  }
  const filePath = input.file_path.trim();
  return filePath.length > 0 ? filePath : undefined;
}

const BACKGROUND_LAUNCH_ID_RE =
  /(?:Command|Process)\s+running\s+(?:in\s+background\s+with\s+ID|with\s+session\s+ID)\s*:?\s*([\w-]+)/i;
const PROCESS_EXITED_RE = /(?:^|\n)\s*Process exited with code \d+/i;
const BACKGROUND_ENDED_STATUS_RE =
  /^(completed|failed|killed|stopped|success|error|done)/i;

function getRecordField(
  value: unknown,
  field: string,
): unknown {
  return isRecord(value) ? value[field] : undefined;
}

function toolResultContent(item: ToolCallItem): string {
  const raw = item.toolResult?.content;
  return typeof raw === "string" ? raw : "";
}

/**
 * Mark completed Bash calls whose command was backgrounded so the row can
 * keep a present-tense header ("Running") until completion evidence appears
 * later in the transcript: a task-notification for the call, a
 * BashOutput/TaskOutput poll reporting completion, a KillShell, or a Codex
 * unified-exec/wait poll whose chunk carries an exit code.
 */
function annotateBackgroundCommands(items: RenderItem[]): RenderItem[] {
  const endedKeys = new Set<string>();
  const addEnded = (...keys: Array<string | false | undefined>) => {
    for (const key of keys) {
      if (key) endedKeys.add(key);
    }
  };

  for (const item of items) {
    if (item.type === "task_notification") {
      if (item.status && BACKGROUND_ENDED_STATUS_RE.test(item.status)) {
        addEnded(item.taskId, item.toolUseId && `tool:${item.toolUseId}`);
      }
      continue;
    }
    if (item.type !== "tool_call") {
      continue;
    }
    const toolName = item.toolName.toLowerCase();
    const input = item.toolInput;
    const structured = item.toolResult?.structured;

    if (toolName === "bashoutput" || toolName === "bash_output") {
      const id = coerceSessionId(
        getRecordField(structured, "shellId") ??
          getRecordField(input, "bash_id"),
      );
      const status = getRecordField(structured, "status");
      const exited = typeof getRecordField(structured, "exitCode") === "number";
      if (id && (exited || status === "completed" || status === "failed")) {
        addEnded(id);
      }
    } else if (toolName === "taskoutput" || toolName === "task_output") {
      const task = getRecordField(structured, "task");
      const status = getRecordField(task, "status");
      if (status === "completed" || status === "failed") {
        addEnded(
          coerceSessionId(
            getRecordField(task, "task_id") ??
              getRecordField(input, "task_id"),
          ),
        );
      }
    } else if (toolName === "killshell" || toolName === "kill_shell") {
      addEnded(
        coerceSessionId(
          getRecordField(structured, "shell_id") ??
            getRecordField(input, "shell_id"),
        ),
      );
    } else if (
      toolName === "writestdin" ||
      toolName === "write_stdin" ||
      toolName === "wait"
    ) {
      const finished =
        typeof getRecordField(structured, "exit_code") === "number" ||
        PROCESS_EXITED_RE.test(toolResultContent(item));
      if (finished) {
        const sessionId = extractSessionIdFromWriteStdinInput(input);
        const cellId = extractCellIdFromWriteStdinInput(input);
        addEnded(
          sessionId && `session:${sessionId}`,
          cellId && `cell:${cellId}`,
        );
      }
    }
  }

  return items.map((item) => {
    if (
      item.type !== "tool_call" ||
      item.status !== "complete" ||
      !isCommandSessionToolName(item.toolName)
    ) {
      return item;
    }

    const content = toolResultContent(item);
    const keys: string[] = [`tool:${item.id}`];
    let backgrounded =
      getRecordField(item.toolInput, "run_in_background") === true;

    const structuredTaskId = coerceSessionId(
      getRecordField(item.toolResult?.structured, "backgroundTaskId"),
    );
    if (structuredTaskId) {
      backgrounded = true;
      keys.push(structuredTaskId, `session:${structuredTaskId}`);
    }
    const launchMatch = content.match(BACKGROUND_LAUNCH_ID_RE);
    if (launchMatch?.[1]) {
      backgrounded = true;
      keys.push(launchMatch[1]);
      if (/session\s+ID/i.test(launchMatch[0])) {
        keys.push(`session:${launchMatch[1]}`);
      }
    }
    const detachedCell = extractDetachedCellId(content);
    if (detachedCell) {
      backgrounded = true;
      keys.push(`cell:${detachedCell}`);
    }

    if (!backgrounded) {
      return item;
    }
    const ended = keys.some((key) => endedKeys.has(key));
    const toolInput = isRecord(item.toolInput) ? item.toolInput : {};
    return {
      ...item,
      toolInput: {
        ...toolInput,
        _backgroundTaskStatus: ended ? "completed" : "running",
      },
    };
  });
}

function enrichWriteStdinWithCommand(items: RenderItem[]): RenderItem[] {
  const sessionToMetadata = new Map<
    string,
    { command?: string; filePath?: string; toolName?: string }
  >();
  const cellToMetadata = new Map<
    string,
    { command?: string; filePath?: string; toolName?: string }
  >();

  return items.map((item) => {
    if (item.type !== "tool_call") {
      return item;
    }

    if (
      isCommandSessionToolName(item.toolName) ||
      isFileSessionToolName(item.toolName)
    ) {
      const sessionId = extractSessionIdFromToolResult(item);
      const cellId = extractCellIdFromToolResult(item);
      if (!sessionId && !cellId) {
        return item;
      }

      const command = isCommandSessionToolName(item.toolName)
        ? extractCommandFromInput(item.toolInput)
        : undefined;
      const filePath = isFileSessionToolName(item.toolName)
        ? extractFilePathFromToolInput(item.toolInput)
        : undefined;

      for (const [id, metadataById] of [
        [sessionId, sessionToMetadata],
        [cellId, cellToMetadata],
      ] as const) {
        if (!id) continue;
        const existing = metadataById.get(id) ?? {};
        metadataById.set(id, {
          command: command ?? existing.command,
          filePath: filePath ?? existing.filePath,
          toolName: item.toolName ?? existing.toolName,
        });
      }
      return item;
    }

    const toolName = item.toolName.toLowerCase();
    if (
      toolName !== "writestdin" &&
      toolName !== "write_stdin" &&
      toolName !== "wait"
    ) {
      return item;
    }

    const sessionId = extractSessionIdFromWriteStdinInput(item.toolInput);
    const cellId = extractCellIdFromWriteStdinInput(item.toolInput);
    const metadata =
      (sessionId ? sessionToMetadata.get(sessionId) : undefined) ??
      (cellId ? cellToMetadata.get(cellId) : undefined);

    // A poll can itself detach into a new cell; carry the origin metadata
    // forward so a later wait on that cell still links to the command.
    const detachedCellId = extractCellIdFromToolResult(item);
    if (detachedCellId && metadata) {
      const existing = cellToMetadata.get(detachedCellId) ?? {};
      cellToMetadata.set(detachedCellId, {
        command: metadata.command ?? existing.command,
        filePath: metadata.filePath ?? existing.filePath,
        toolName: metadata.toolName ?? existing.toolName,
      });
    }

    // A wait's collected script output may reveal the shell session the
    // script started (e.g. a printed SESSION_ID=N line); bridge the origin
    // metadata to that session so later polls of it link to the command.
    const declaredSessionId = extractSessionIdFromToolResult(item);
    if (declaredSessionId && metadata) {
      const existing = sessionToMetadata.get(declaredSessionId) ?? {};
      sessionToMetadata.set(declaredSessionId, {
        command: metadata.command ?? existing.command,
        filePath: metadata.filePath ?? existing.filePath,
        toolName: metadata.toolName ?? existing.toolName,
      });
    }

    if (!metadata) {
      return item;
    }

    let toolInput = item.toolInput;
    if (metadata.command) {
      toolInput = withLinkedCommand(toolInput, metadata.command);
    }
    if (metadata.filePath) {
      toolInput = withLinkedFilePath(toolInput, metadata.filePath);
    }
    if (metadata.toolName) {
      toolInput = withLinkedToolName(toolInput, metadata.toolName);
    }

    if (toolInput === item.toolInput) {
      return item;
    }

    return {
      ...item,
      toolInput,
    };
  });
}

function isShellPollToolName(toolName: string): boolean {
  const normalized = toolName.toLowerCase();
  return (
    normalized === "writestdin" ||
    normalized === "write_stdin" ||
    normalized === "wait"
  );
}

/**
 * One logical shell poll can span several transcript records: the poll's
 * script detaches into a cell ("Script running with cell ID N") and a later
 * `wait` on that cell collects the actual result — possibly detaching again
 * before it does. Rendering each record separately reads as a puzzling
 * "still running → cell N" row followed by an unlabeled result row, so fold
 * the chain into the originating poll: it keeps its input and linkage and
 * takes the final wait's result. A wait that never resolves in the loaded
 * transcript leaves the poll honestly "still running".
 *
 * Single scan plus a prebuilt cell-id index: cell ids restart within a
 * rollout, so a consumer takes the first unconsumed wait AFTER its own
 * position, never an earlier reuse.
 */
function coalesceDetachedPollContinuations(items: RenderItem[]): RenderItem[] {
  const waitIndicesByCell = new Map<string, number[]>();
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    if (item?.type !== "tool_call" || !isShellPollToolName(item.toolName)) {
      continue;
    }
    const cellId = extractCellIdFromWriteStdinInput(item.toolInput);
    if (!cellId) continue;
    const list = waitIndicesByCell.get(cellId);
    if (list) {
      list.push(index);
    } else {
      waitIndicesByCell.set(cellId, [index]);
    }
  }
  if (waitIndicesByCell.size === 0) {
    return items;
  }

  const consumed = new Set<number>();
  const takeNextWaitOnCell = (cellId: string, after: number): number => {
    const list = waitIndicesByCell.get(cellId);
    if (!list) return -1;
    for (const index of list) {
      if (index > after && !consumed.has(index)) return index;
    }
    return -1;
  };

  const result: RenderItem[] = [];
  for (let i = 0; i < items.length; i++) {
    if (consumed.has(i)) continue;
    let item = items[i];
    if (item === undefined) continue;

    if (item.type === "tool_call" && isShellPollToolName(item.toolName)) {
      let cellId = extractCellIdFromToolResult(item);
      let after = i;
      while (cellId) {
        const waitIndex = takeNextWaitOnCell(cellId, after);
        if (waitIndex < 0) break;
        const wait = items[waitIndex];
        if (wait?.type !== "tool_call") break;
        consumed.add(waitIndex);
        after = waitIndex;
        item = {
          ...item,
          status: wait.status,
          toolResult: wait.toolResult,
        };
        cellId =
          wait.toolResult !== undefined
            ? extractCellIdFromToolResult(wait)
            : undefined;
      }
    }
    result.push(item);
  }
  return result;
}

/**
 * Drop completed shell polls that carry no information a reader could act
 * on: a pure poll (no stdin chars) that produced no output, no error, no
 * exit code, and — after linkage enrichment — no associated command, file,
 * or origin tool. Such rows read as a bare "Shell — No output" with no
 * discoverable context. Pending polls stay visible as live activity, and a
 * poll with any linked context keeps its row.
 */
function hideContextFreeEmptyShellPolls(items: RenderItem[]): RenderItem[] {
  return items.filter((item) => {
    if (item.type !== "tool_call" || item.status !== "complete") {
      return true;
    }
    const toolName = item.toolName.toLowerCase();
    if (
      toolName !== "writestdin" &&
      toolName !== "write_stdin" &&
      toolName !== "wait"
    ) {
      return true;
    }
    if (item.toolResult?.isError) {
      return true;
    }

    const input = isRecord(item.toolInput) ? item.toolInput : {};
    const chars = typeof input.chars === "string" ? input.chars : "";
    const hasContext =
      chars.length > 0 ||
      [input.linked_command, input.linked_file_path, input.linked_tool_name]
        .some((value) => typeof value === "string" && value.trim().length > 0);
    if (hasContext) {
      return true;
    }

    const content = item.toolResult?.content ?? "";
    const parsed = parseShellToolOutput(content);
    const exitCode =
      getCommandResultMeta(item.toolResult?.structured).exitCode ??
      parsed.exitCode;
    return parsed.output.trim().length > 0 || exitCode !== undefined;
  });
}
